import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { produce } from 'immer'
import { camelCase, find, keys, map } from 'lodash'
import { RootState } from '../../../store'
import barcodeService from '../../../services/barcode.service'
import alertService from '../../../services/alert.service'
import customLabelApi, { CustomLabelData } from '../../../apis/customLabel.api'
import metaApi, { MetaData } from '../../../apis/meta.api'
import {
  replaceElementText,
  appendElementText,
  replaceBarcodeElementImage
} from '../../../views/LabelBuilder/LabelBuilder.slice'
import Textarea from '../../Textarea'

interface Attribute {
  key: string
  name: string
}

interface Props {}

type CombinedProps = Props & ConnectedProps<typeof connector>

interface State {
  customLabel: CustomLabelData | null
  itemMetas: MetaData[]
  categoryMetas: MetaData[]
}

class ContentProperty extends Component<CombinedProps, State> {
  constructor (props: CombinedProps) {
    super(props)
    this.state = {
      customLabel: null,
      itemMetas: [],
      categoryMetas: []
    }
  }

  baseAttributes:Attribute[] = [
    { key: 'siteName', name: 'Site name' },
    { key: 'siteAddress', name: 'Site address' },
    { key: 'username', name: 'Username' },
    { key: 'autoIncrement', name: 'Auto increment' },
    { key: 'currentDate', name: 'Current date' },
    { key: 'currentTime', name: 'Current time' }
  ]

  inventoryAttributes:Attribute[] = [
    { key: 'itemCode', name: 'Item code' },
    { key: 'itemDescription', name: 'Item description' },
    { key: 'itemCategory', name: 'Item category' },
    { key: 'caseCode', name: 'Case code' },
    { key: 'caseNumber', name: 'Case number' },
    { key: 'caseQuantity', name: 'Case quantity' },
    { key: 'palletCode', name: 'Pallet code' },
    { key: 'palletNumber', name: 'Pallet number' },
    { key: 'workOrderCode', name: 'Work order code' },
    { key: 'purchaseOrderCode', name: 'Purchase order code' },
    { key: 'lineNumber', name: 'Line number' },
    { key: 'createdDate', name: 'Created date' },
    { key: 'netWeight', name: 'Net weight' },
    { key: 'grossWeight', name: 'Gross weight' },
    { key: 'actualWeight', name: 'Actual weight' },
    { key: 'siteLPN', name: 'Site LPN' },
    { key: 'customerLPN', name: 'Customer LPN' },
    { key: 'customerPartNumber', name: 'Customer part number' },
    { key: 'vendorPartNumber', name: 'Vendor  part number' },
    ...this.baseAttributes
  ]

  get customLabelAttributes ():Attribute[] {
    const { customLabel } = this.state
    if (!customLabel) return []
    const attributes = map(keys(customLabel.attributes), attribute => {
      return { key: attribute, name: attribute }
    })
    return [
      ...attributes,
      ...this.baseAttributes
    ]
  }

  get itemMetaAttributes (): Attribute[] {
    const { itemMetas } = this.state
    const attributes = map(itemMetas, meta => {
      const attribute = `meta:${camelCase(meta.value)}`
      return { key: attribute, name: attribute }
    })
    return [
      ...this.inventoryAttributes,
      ...attributes,
    ]
  }

  get categoryMetaAttributes (): Attribute[] {
    const { categoryMetas } = this.state
    const attributes = map(categoryMetas, meta => {
      const attribute = `meta:${camelCase(meta.value)}`
      return { key: attribute, name: attribute }
    })
    return [
      ...attributes,
    ]
  }
  
  

  get attributes ():Attribute[] {
    const { target, customLabelId, itemId, categoryId } = this.props
    if (target === 'customLabel' && customLabelId) {
      return this.customLabelAttributes
    } else if (target === 'item' && itemId) {
      return this.itemMetaAttributes
    } else if (target === 'category' && categoryId) {
      return [...this.categoryMetaAttributes,
        ...this.itemMetaAttributes]
    } else {
      return this.inventoryAttributes
    }
  }

  handleInputChange = (name: string, value: string) => {
    const { selectedElement } = this.props
    if (!selectedElement) return
    if (name === 'content') {
      this.props.replaceElementText(selectedElement.id, value)
      if (selectedElement.type === 'barcode') {
        barcodeService.generateBarcode(selectedElement.format, value, (image) => {
          this.props.replaceBarcodeElementImage(selectedElement.id, image)
        })
      }
    }
  }

  handleAttributeClick = (attribute: Attribute) => {
    const { selectedElement } = this.props
    if (!selectedElement) return
    this.props.appendElementText(selectedElement.id, attribute.key)
    if (selectedElement.type === 'barcode') {
      const value = `${selectedElement.text} ${attribute.key}`
      alertService.info('Dynamic reference has been chosen. It assumed the string matches the 1D/2D format')
      barcodeService.generateBarcode(selectedElement.format, value, (image) => {
        this.props.replaceBarcodeElementImage(selectedElement.id, image)
      })
    }
  }

  readCustomLabel () {
    const { customLabelId } = this.props
    if (!customLabelId) return
    customLabelApi.read(customLabelId, customLabel => {
      if (!customLabel) return
      this.setState({ customLabel })
    })
  }

  listItemMeta () {
    const { itemId } = this.props
    if (!itemId) return
    metaApi.listByItemAttribute(itemId, itemMetas => {
      if (!itemMetas) return
      this.setState({ itemMetas })
    })
  }

  listCategoryMeta () {
    const { categoryId } = this.props
    if (!categoryId) return
    metaApi.listByCategoryAttribute(categoryId, categoryMetas => {
      if (!categoryMetas) return
      this.setState({ categoryMetas })
    })
  }

  componentDidMount () {
    this.readCustomLabel()
    this.listItemMeta()
    this.listCategoryMeta()
  }

  render () {
    const { selectedElement } = this.props
    if (!selectedElement || selectedElement.type === 'image' || selectedElement.type === 'line') return null
    return (
      <div className='mb-4'>
        <Textarea
          className='mb-0'
          name="content"
          value={selectedElement.text}
          label="Content"
          rows={2}
          onChange={this.handleInputChange} />
        <div className='flex flex-wrap gap-1.5'>
          {this.attributes.map(attribute => {
            return (
              <div
                className='bg-blue-100 text-xs px-2 cursor-pointer rounded-sm text-gray-800'
                key={attribute.key}
                onClick={() => this.handleAttributeClick(attribute)} >
                {attribute.name}
              </div>
            )
          })}
        </div>
      </div>
    )
  }
}

function mapStateToProps (state: RootState) {
  const { labelBuilder } = produce(state, draft => draft)

  console.log('labelBuilder.markup:', labelBuilder.markup)

  const { elements, customLabelId, target, itemId, categoryId } = labelBuilder.markup
  const selectedElement = find(elements, element => {
    return element.id === labelBuilder.markup.selectedElement
  })
  return { selectedElement, customLabelId, target, itemId, categoryId }
}

const mapDispatchToProps = {
  replaceElementText,
  appendElementText,
  replaceBarcodeElementImage
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ContentProperty)
